// AppointmentBookingModal.tsx
import React, { useState, useEffect } from "react";
import { Button, Space, Alert, Typography, Row, Col, Tag, Progress, Avatar, Radio, Divider, Modal } from "antd";
import { DetailDrawer, InlineLoading, EmptyState } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";
import {  UserOutlined, CheckCircleOutlined, ArrowLeftOutlined, ArrowRightOutlined, CreditCardOutlined, PlusOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../../utils/auth/sessionUser";

import { Appointment, AppointmentSaveRequest, AppointmentType, AvailablePractitioner, AvailableDay, TimeSlot, BookingPolicy } from "../../../types/appointments/AppointmentTypes";
import { User } from "../../../types/admin/UserTypes";
import { PaymentStatus, PaymentType, StripeCustomer, StripePaymentMethod } from "../../../types/finance/FinanceTypes";

import { fetchAvailableTimes, createAppointment, fetchBookableAppointmentTypesPatient } from "../../../api/healthcare/Appointment/AppointmentApi";
import { isWithinBookingWindow } from "../../../utils/healthcare/bookingWindow";
import { generateConsultationLink } from "../../../utils/consult/consultationLink";
import { DateTimeUtils, parseUTCToLocal, formatLocalTime } from "../../../utils/userPrefs/DateTimeUtils";
import { getFinanceCustomer, createFinanceCustomer, listPaymentMethods, createPaymentPublic } from "../../../api/finance/FinanceApi";
import { checkFinanceIntegrationForCompany } from "../../../api/admin/integrationApi";
import AddPaymentMethodModal from "../../../components/finance/AddPaymentMethodModal";
import ScreeningStep from "../../../components/booking/ScreeningStep";
import { ScreeningPass } from "../../../types/appointments/ScreeningTypes";
import { SCREENING_REQUIRED } from "../../../utils/healthcare/screeningOutcome";
import "./AppointmentBookingModal.css";

const { Title, Text, Paragraph } = Typography;

export interface PatientInfo {
	first_name: string;
	last_name: string;
	email: string;
	phone: string;
}

interface AppointmentBookingModalProps {
	visible: boolean;
	onHide: () => void;
	onSuccess: (appointmentId?: number) => void;
}

const AppointmentBookingModal: React.FC<AppointmentBookingModalProps> = ({ visible, onHide, onSuccess }) => {
	const user: User = getSessionUser();
	const { showError, showSuccess, showWarning, showInfo } = useNotifications();

	// Step management (0 = appointment type, 1 = consultant, 2 = date/time, 3 = payment (if needed), 4 = review)
	const [currentStep, setCurrentStep] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);

	// Appointment types
	const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
	const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);
	const [loadingTypes, setLoadingTypes] = useState(false);

	// Payment state
	const [financeEnabled, setFinanceEnabled] = useState(false);
	const [financeCustomer, setFinanceCustomer] = useState<StripeCustomer | null>(null);
	const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>([]);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
	const [addPaymentModalVisible, setAddPaymentModalVisible] = useState(false);
	const [loadingPayment, setLoadingPayment] = useState(false);
	const [paymentIntentId, setPaymentIntentId] = useState<string>("");
	const [paymentRefreshTrigger, setPaymentRefreshTrigger] = useState(0);

	// Pre-filled patient info since user is logged in
	const patientInfo: PatientInfo = {
		first_name: user.first_name || "",
		last_name: user.last_name || "",
		email: user.email || "",
		phone: user.phone || "",
	};

	const [appointmentData, setAppointmentData] = useState<Partial<AppointmentSaveRequest>>({
		status: "Pending",
		location: "",
		category: "Established Patient",
		title: "",
		date: undefined,
		start_time: undefined,
		end_time: undefined,
		practitioner_id: "",
		appointment_type_id: undefined,
		consultation_link: "",
	});

	const [practitioners, setPractitioners] = useState<AvailablePractitioner[]>([]);
	const [selectedPractitioner, setSelectedPractitioner] = useState<AvailablePractitioner | null>(null);
	const [selectedDate, setSelectedDate] = useState<string>("");
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
	const [availableDays, setAvailableDays] = useState<AvailableDay[]>([]);
	const [availableTimesForDate, setAvailableTimesForDate] = useState<TimeSlot[]>([]);
	const [currentMonth, setCurrentMonth] = useState(new Date());

	// The clinic's PUBLIC booking policy (timezone + booking_window_days) off the booking read — the
	// same block `BookingForm` consumes. Both wizards must bound their calendars identically.
	const [bookingPolicy, setBookingPolicy] = useState<BookingPolicy | null>(null);

	// Expanded appointment type description
	const [previewType, setPreviewType] = useState<AppointmentType | null>(null);
	const [previewPractitioner, setPreviewPractitioner] = useState<AvailablePractitioner | null>(null);

	// Pre-booking screening (S9, ClickUp 86d439wvy). The pass is per appointment type and lives
	// only here — it is a short-lived capability and a patient may be on a shared device.
	const [screeningToken, setScreeningToken] = useState<string | undefined>(undefined);
	const [screeningBlocked, setScreeningBlocked] = useState<string | null>(null);
	const [screeningResumeStep, setScreeningResumeStep] = useState<number | null>(null);

	const hasScreeningStep = Boolean(selectedAppointmentType?.has_screening);
	const screeningRequired = hasScreeningStep && !screeningToken;
	// Screening is inserted for DISPLAY at index 1; `currentStep` keeps the numbering the ten
	// hard-coded call sites below were written against, so no downstream index shifts.
	const displayStep = !hasScreeningStep || currentStep === 0 ? currentStep : screeningRequired ? 1 : currentStep + 1;

	useEffect(() => {
		if (visible) {
			console.log("AppointmentBookingModal: visible changed to", visible);

			setAppointmentData((prev) => ({ ...prev }));
			loadAppointmentTypes();
			initializeFinance();
		}
	}, [visible]);

	useEffect(() => {
		if (selectedPractitioner) {
			setAvailableDays(selectedPractitioner.Available_times);
			setSelectedDate("");
			setSelectedTimeSlot(null);
			setAvailableTimesForDate([]);
			setAppointmentData((prev) => ({
				...prev,
				practitioner_id: selectedPractitioner.practitioner_id,
				date: undefined,
				start_time: undefined,
				end_time: undefined,
			}));
		} else {
			setAvailableDays([]);
			setSelectedDate("");
			setSelectedTimeSlot(null);
			setAvailableTimesForDate([]);
		}
	}, [selectedPractitioner]);

	useEffect(() => {
		if (selectedDate && availableDays.length > 0) {
			const dayData = availableDays.find((day) => day.date === selectedDate);
			if (dayData) {
				setAvailableTimesForDate(dayData.slots);
				setAppointmentData((prev) => ({
					...prev,
					date: new Date(selectedDate),
				}));
			} else {
				setAvailableTimesForDate([]);
			}
			setSelectedTimeSlot(null);
			setAppointmentData((prev) => ({
				...prev,
				start_time: undefined,
				end_time: undefined,
			}));
		} else {
			setAvailableTimesForDate([]);
		}
	}, [selectedDate, availableDays]);

	useEffect(() => {
		if (selectedTimeSlot && selectedDate && selectedAppointmentType) {
			const startTime = parseUTCToLocal(selectedTimeSlot.datetime);

			if (startTime) {
				const endTime = new Date(startTime);
				endTime.setMinutes(endTime.getMinutes() + selectedAppointmentType.duration_minutes);

				setAppointmentData((prev) => ({
					...prev,
					start_time: startTime,
					end_time: endTime,
				}));
			}
		}
	}, [selectedTimeSlot, selectedDate, selectedAppointmentType]);

	useEffect(() => {
		if (paymentRefreshTrigger > 0 && financeCustomer) {
			loadPaymentMethods();
		}
	}, [paymentRefreshTrigger]);

	const formatLocalDateString = (date: Date): string => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	};

	const isDateToday = (date: Date): boolean => {
		const today = new Date();
		return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
	};

	const parseLocalDateString = (dateStr: string): Date => {
		const [year, month, day] = dateStr.split("-").map(Number);
		return new Date(year, month - 1, day, 0, 0, 0, 0);
	};

	const initializeFinance = async () => {

		try {

			const stripe = await checkFinanceIntegrationForCompany();
			setFinanceEnabled(Boolean(stripe.isActive));

			if (stripe.isActive && user.stripe_customer_id) {
				try {
					const customer = await getFinanceCustomer(user.id);
					setFinanceCustomer(customer.stripe_customer);
					await loadPaymentMethods(customer.stripe_customer.id);
				} catch (error) {
					console.error("Error loading existing Stripe customer:", error);
				}
			}
		} catch (error) {
			console.error("Error initializing Stripe:", error);
		}
	};

	const loadOrcreateFinanceCustomer = async () => {
		if (!financeEnabled) return;

		setLoadingPayment(true);
		try {
			// TODO: get finance customer info based on the integration type, currently stripe is the only option
			if (user.stripe_customer_id) {
				try {
					const customer = await getFinanceCustomer(user.id);
					setFinanceCustomer(customer.stripe_customer);
					await loadPaymentMethods(customer.stripe_customer.id);
					return;
				} catch (error) {
					console.error("Existing customer not found, creating new:", error);
				}
			}

			// Create new customer
			const newCustomer = await createFinanceCustomer({
				userId: user.id.toString(),
				email: user.email,
				name: `${user.first_name} ${user.last_name}`,
				phone: user.phone,
			});

			setFinanceCustomer(newCustomer.stripe_customer);
			await loadPaymentMethods(newCustomer.stripe_customer.id);
		} catch (error: any) {
			console.error("Error with Stripe customer:", error);
			showWarning("Unable to set up payment. Please try again.");
		} finally {
			setLoadingPayment(false);
		}
	};

	const loadPaymentMethods = async (customerId?: string) => {
		const custId = customerId || financeCustomer?.id;
		if (!custId) return;

		try {
			const methods = await listPaymentMethods(custId);
			setPaymentMethods(methods);

			// Auto-select default payment method if available
			const defaultMethod = methods.find((m) => m.id === financeCustomer?.invoice_settings?.default_payment_method);
			if (defaultMethod) {
				setSelectedPaymentMethod(defaultMethod.id);
			}
		} catch (error) {
			console.error("Error loading payment methods:", error);
		}
	};

	const requiresPayment = (): boolean => {
		if (!selectedAppointmentType?.default_price) return false;
		const price = parseFloat(selectedAppointmentType.default_price);
		return price > 0;
	};

	const processPayment = async (): Promise<boolean> => {
		if (!requiresPayment()) return true;

		if (!selectedPaymentMethod) {
			showError("Please select a payment method");
			return false;
		}

		if (!financeCustomer) {
			showError("Payment setup incomplete");
			return false;
		}

		setLoadingPayment(true);
		try {
			const amount = Math.floor(parseFloat(selectedAppointmentType!.default_price!) * 100);

			// Create payment intent
			const payment = await createPaymentPublic({
				userId: user.id.toString(),
				type: PaymentType.BOOKING,
				amount: amount,
				currency: "AUD",
				paymentMethodId: selectedPaymentMethod,
				description: `${selectedAppointmentType!.name} - ${patientInfo.first_name} ${patientInfo.last_name}`,
				bookingId: `booking-${crypto.randomUUID()}`,
				metadata: {
					appointment_type: selectedAppointmentType!.name,
					patient_name: `${patientInfo.first_name} ${patientInfo.last_name}`,
					patient_email: patientInfo.email,
					patient_id: user.id.toString(),
				},
			});

			setPaymentIntentId(payment.metadata.stripe_payment_intent_id);

			if (payment.status === PaymentStatus.COMPLETED) {
				showSuccess("Payment processed successfully");
				return true;
			} else {
				showError("Payment requires additional action");
				return false;
			}
		} catch (error: any) {
			console.error("Payment error:", error);
			showError(error.message || "Payment failed");
			return false;
		} finally {
			setLoadingPayment(false);
		}
	};

	const loadAppointmentTypes = async () => {

		setLoadingTypes(true);
		try {
			const types = await fetchBookableAppointmentTypesPatient(user.id);
			setAppointmentTypes(types);
		} catch (error) {
			console.error("Error loading appointment types:", error);
			showError("Failed to load appointment types");
		} finally {
			setLoadingTypes(false);
		}
	};

	const handleAppointmentTypeSelect = async (type: AppointmentType) => {
		// The pass is issued per appointment type, so switching type discards it.
		const heldPass = screeningToken && selectedAppointmentType?.id === type.id ? screeningToken : undefined;
		setScreeningToken(heldPass);

		setSelectedAppointmentType(type);
		setAppointmentData((prev) => ({
			...prev,
			appointment_type_id: type.id,
			location: type.telehealth_enabled ? "online" : "in-person",
			title: type.name,
		}));

		// Step 1 renders the questionnaire, not the consultant list, while no pass is held —
		// so nothing downstream is reachable and no availability is read.
		if (type.has_screening && !heldPass) {
			setCurrentStep(1);
			return;
		}

		await loadAvailableTimes(type.id, heldPass);
		setCurrentStep(1);
	};

	const loadAvailableTimes = async (appointmentTypeId: number, screeningPass?: string) => {
		setIsLoading(true);
		try {
			// Third argument only when a pass is held, so a non-screened type's call is unchanged.
			const data = screeningPass ? await fetchAvailableTimes(appointmentTypeId, undefined, screeningPass) : await fetchAvailableTimes(appointmentTypeId);
			setPractitioners(data.practitioners);
			// Present on both 200 paths, absent on the 404 — never assume it is there.
			setBookingPolicy(data.policy ?? null);

			if (data.practitioners.length === 0) {
				showWarning("No practitioners available for this appointment type");
			}
		} catch (error) {
			console.error("Error loading available times:", error);
			setError("Failed to load available appointment times. Please try again.");
			showError("Failed to load available times");
		} finally {
			setIsLoading(false);
		}
	};

	const submitAppointment = async () => {
		try {
			const result = screeningToken ? await createAppointment(appointmentData, patientInfo, screeningToken) : await createAppointment(appointmentData, patientInfo);
			// POST /patient/bookings answers { message, data: { appointment_id, ... } }.
			return { success: true, appointmentId: result?.data?.appointment_id as number | undefined };
		} catch (error) {
			throw error;
		}
	};

	const handleSubmit = async () => {
		setError("");

		if (!patientInfo.first_name || !patientInfo.last_name || !patientInfo.email) {
			setError("Please fill in all patient information fields");
			showWarning("Please fill in all required fields");
			return;
		}

		if (!appointmentData.practitioner_id || !appointmentData.date || !appointmentData.start_time) {
			setError("Please complete all appointment details");
			return;
		}

		if (!appointmentData.category || !appointmentData.appointment_type_id) {
			setError("Please complete all appointment details");
			return;
		}

		// Process payment if required
		if (requiresPayment() && financeEnabled) {
			const paymentSuccess = await processPayment();
			if (!paymentSuccess) {
				setError("Payment failed. Please try again.");
				return;
			}
		}

		if (selectedAppointmentType?.telehealth_enabled) {
			appointmentData.consultation_link = generateConsultationLink();
		}

		try {
			setIsSubmitting(true);
			const result = await submitAppointment();
			if (result.success) {
				showSuccess("Appointment booked successfully!");
				resetForm();
				onSuccess(result.appointmentId);
				setTimeout(() => {
					onHide();
				}, 1500);
			}
		} catch (err: any) {
			// A pass that expired while the patient browsed. Send them back to the questionnaire
			// rather than dead-ending on a review screen they can never submit.
			const body = err?.response?.data || {};
			if (err?.response?.status === 422 && (body.error === SCREENING_REQUIRED || body.code === SCREENING_REQUIRED)) {
				setScreeningToken(undefined);
				setScreeningResumeStep(currentStep);
				setCurrentStep(1);
				setError(body.message || "Your screening answers have expired. Please answer the questions again.");
				return;
			}

			setError(err.message || "An error occurred while booking your appointment");
			showError(err.message || "Failed to book appointment");
		} finally {
			setIsSubmitting(false);
		}
	};

	const resetForm = () => {
		setCurrentStep(0);
		setAppointmentData({
			status: "Pending",
			location: "",
			category: "Established Patient",
			title: "",
			date: undefined,
			start_time: undefined,
			end_time: undefined,
			practitioner_id: "",
			appointment_type_id: undefined,
			consultation_link: "",
		});
		setSelectedAppointmentType(null);
		setSelectedPractitioner(null);
		setSelectedDate("");
		setSelectedTimeSlot(null);
		setAvailableDays([]);
		setAvailableTimesForDate([]);
		setBookingPolicy(null);
		setPreviewType(null);
		setScreeningToken(undefined);
		setScreeningBlocked(null);
		setScreeningResumeStep(null);
		setError("");
		setSelectedPaymentMethod("");
		setFinanceCustomer(null);
		setPaymentMethods([]);
	};

	const goToStep = (step: number) => {
		setCurrentStep(step);
		setError("");
	};

	const handleScreeningPass = async (pass: ScreeningPass) => {
		setScreeningToken(pass.screening_token);
		setError("");

		await loadAvailableTimes(selectedAppointmentType!.id, pass.screening_token);

		if (screeningResumeStep !== null) {
			setCurrentStep(screeningResumeStep);
			setScreeningResumeStep(null);
		}
	};

	// The blocked screen's only exit. Everything the previous type set has to go, or the next
	// type inherits its practitioners, slots and pass.
	const resetScreening = () => {
		setScreeningBlocked(null);
		setScreeningToken(undefined);
		setScreeningResumeStep(null);
		setSelectedAppointmentType(null);
		setSelectedPractitioner(null);
		setPractitioners([]);
		setSelectedDate("");
		setSelectedTimeSlot(null);
		setAvailableDays([]);
		setAvailableTimesForDate([]);
		setBookingPolicy(null);
		setAppointmentData((prev) => ({ ...prev, appointment_type_id: undefined, title: "", location: "" }));
		setCurrentStep(0);
		setError("");
	};

	const handleConsultantSelect = (practitioner: AvailablePractitioner) => {
		setSelectedPractitioner(practitioner);
		goToStep(2);
	};

	const handleDateSelect = (date: string) => {
		setSelectedDate(date);
		setAppointmentData((prev) => ({
			...prev,
			date: parseLocalDateString(date),
		}));
	};

	const handleTimeSlotSelect = async (timeSlot: TimeSlot) => {
		const enhancedTimeSlot = {
			...timeSlot,
			localDateTime: DateTimeUtils.parseUTCToLocal(timeSlot.datetime),
			displayTime: DateTimeUtils.formatLocalTime(timeSlot.datetime),
		};
		setSelectedTimeSlot(enhancedTimeSlot);

		// If payment is required, go to payment step, otherwise go to review
		if (requiresPayment() && financeEnabled) {
			await loadOrcreateFinanceCustomer();
			goToStep(3);
		} else {
			goToStep(3);
		}
	};

	const getAvailableDates = () => {
		if (!selectedPractitioner) return [];
		return selectedPractitioner.Available_times.map((day) => day.date);
	};

	const getSteps = () => {
		const flowSteps = [{ label: "Type" }, { label: "Consultant" }, { label: "Date & Time" }];
		const baseSteps = hasScreeningStep ? [flowSteps[0], { label: "Screening" }, ...flowSteps.slice(1)] : flowSteps;

		if (requiresPayment() && financeEnabled) {
			return [...baseSteps, { label: "Payment" }, { label: "Review" }];
		}

		return [...baseSteps, { label: "Review" }];
	};

	const renderProgressBar = () => {
		const steps = getSteps();
		const progress = ((displayStep + 1) / steps.length) * 100;

		return (
			<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
				<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
					{steps.map((step, index) => (
						<div
							key={index}
							style={{
								fontSize: 12,
								color: index <= displayStep ? "#1890ff" : "#999",
								fontWeight: index <= displayStep ? "bold" : "normal",
							}}
						>
							{step.label}
						</div>
					))}
				</div>
				<Progress percent={progress} showInfo={false} size="small" />
			</div>
		);
	};

	const renderAppointmentTypeSelection = () => {
		const price = (type: AppointmentType) => {
			if (!type.default_price) return 0;
			return parseFloat(type.default_price);
		};

		return (
			<div>
				<Title level={4} style={{ marginBottom: 8 }}>
					Select Appointment Type
				</Title>
				<Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 16 }}>
					Choose the type of appointment you need
				</Text>

				{loadingTypes ? (
					<InlineLoading minHeight={160} />
				) : appointmentTypes.length === 0 ? (
					<EmptyState title="No appointment types available" />
				) : (
					<>
					<Space direction="vertical" size="middle" style={{ width: "100%" }}>
						{appointmentTypes.map((type) => {
						const typePrice = price(type);

						return (
							<div
								key={type.id}
								className={`appointment-type-card ${selectedAppointmentType?.id === type.id ? "selected-appointment-type" : ""}`}
								style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", cursor: "pointer", marginBottom: 8 }}
								onClick={() => (type.description ? setPreviewType(type) : handleAppointmentTypeSelect(type))}
							>
								<Row align="middle" gutter={[12, 12]}>
									<Col>
										<div
											style={{
												width: 40,
												height: 40,
												borderRadius: "8px",
												backgroundColor: type.color || "#1890ff",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												color: "white",
											}}
										>
											<i className={type.telehealth_enabled ? "fas fa-video" : "fas fa-stethoscope"} />
										</div>
									</Col>
									<Col flex={1}>
										<Title level={5} style={{ margin: 0, marginBottom: 4 }}>
											{type.name}
										</Title>
										<Space size="small" style={{ marginTop: 4 }}>
											<Tag color="blue">{type.duration_minutes} min</Tag>
											{type.telehealth_enabled && <Tag color="green">Online</Tag>}
											{typePrice > 0 && <Tag color="gold">${typePrice.toFixed(2)}</Tag>}
										</Space>
									</Col>
									{type.description && (
										<Col>
											<i className="fas fa-info-circle" style={{ fontSize: 14, color: "#999" }} />
										</Col>
									)}
								</Row>
							</div>
						);
					})}
				</Space>

				<Modal
					open={!!previewType}
					onCancel={() => setPreviewType(null)}
					width={400}
					footer={null}
					title={
						<Space>
							<div
								style={{
									width: 32,
									height: 32,
									borderRadius: "6px",
									backgroundColor: previewType?.color || "#1890ff",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: "white",
								}}
							>
								<i className={previewType?.telehealth_enabled ? "fas fa-video" : "fas fa-stethoscope"} style={{ fontSize: 13 }} />
							</div>
							{previewType?.name}
						</Space>
					}
				>
					{previewType && (
						<>
							<Space size="small" style={{ marginBottom: 12 }}>
								<Tag color="blue">{previewType.duration_minutes} min</Tag>
								{previewType.telehealth_enabled && <Tag color="green">Online</Tag>}
								{price(previewType) > 0 && <Tag color="gold">${price(previewType).toFixed(2)}</Tag>}
							</Space>
							<Paragraph style={{ fontSize: 13, color: "#555", marginBottom: 20 }}>{previewType.description}</Paragraph>
							<div style={{ textAlign: "right" }}>
								<Button
									type="primary"
									onClick={() => {
										setPreviewType(null);
										handleAppointmentTypeSelect(previewType);
									}}
								>
									Select
								</Button>
							</div>
						</>
					)}
				</Modal>
					</>
				)}
			</div>
		);
	};

	const renderConsultantCard = (practitioner: AvailablePractitioner) => {
		const isSelected = selectedPractitioner?.practitioner_id === practitioner.practitioner_id;
		let availableSlots = 0;
		let availabilityText = "";
		availableSlots = practitioner.Available_times.reduce((sum, day) => sum + day.slots.length, 0);
		if (availableSlots >= 5) {
			availabilityText = "Good availability";
		} else if (availableSlots > 0) {
			availabilityText = "Limited availability";
		} else {
			availabilityText = "No availability";
		}

		return (
			<div key={practitioner.practitioner_id} style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", cursor: "pointer", marginBottom: 8 }} onClick={() => setPreviewPractitioner(practitioner)}>
				<Row align="middle" gutter={[12, 12]}>
					<Col>
						<Avatar src={practitioner.profile_picture || "/assets/images/default-profile.png"} size={48} />
					</Col>
					<Col flex={1}>
						<Title level={5} style={{ margin: 0, marginBottom: 4 }}>
							{practitioner.first_name} {practitioner.last_name}
						</Title>
						{practitioner.primary_specialty && (
							<Text type="secondary" style={{ fontSize: 12, display: "block" }}>
								{practitioner.primary_specialty}
							</Text>
						)}
						<Text style={{ fontSize: 11, color: "#52c41a" }}>
							<CheckCircleOutlined /> {availabilityText}
						</Text>
					</Col>
					<Col>
						<i className="fas fa-info-circle" style={{ fontSize: 14, color: "#999" }} />
					</Col>
				</Row>
			</div>
		);
	};

	const renderPractitionerModal = () => {
		if (!previewPractitioner) return null;
		const p = previewPractitioner;
		const availableSlots = p.Available_times.reduce((sum, day) => sum + day.slots.length, 0);
		const availabilityText = availableSlots >= 5 ? "Good availability" : availableSlots > 0 ? "Limited availability" : "No availability";

		return (
			<Modal
				open={!!previewPractitioner}
				onCancel={() => setPreviewPractitioner(null)}
				width={480}
				footer={null}
				title={
					<Space align="center">
						<Avatar src={p.profile_picture || "/assets/images/default-profile.png"} size={40} />
						<div>
							<div style={{ fontWeight: 600, lineHeight: 1.2 }}>{p.first_name} {p.last_name}</div>
							{p.primary_specialty && <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>{p.primary_specialty}</div>}
						</div>
					</Space>
				}
			>
				<Space direction="vertical" size="small" style={{ width: "100%", marginBottom: 16 }}>
					{p.years_of_experience !== undefined && (
						<Text style={{ fontSize: 13 }}><strong>Experience:</strong> {p.years_of_experience} {p.years_of_experience === 1 ? "year" : "years"}</Text>
					)}
					{p.qualifications?.length > 0 && (
						<div>
							<Text strong style={{ fontSize: 13 }}>Qualifications</Text>
							<div style={{ marginTop: 4 }}>
								{p.qualifications.map((q) => <Tag key={q} style={{ marginBottom: 4 }}>{q}</Tag>)}
							</div>
						</div>
					)}
					{p.specialties?.length > 0 && (
						<div>
							<Text strong style={{ fontSize: 13 }}>Specialties</Text>
							<div style={{ marginTop: 4 }}>
								{p.specialties.map((s) => <Tag key={s} color="blue" style={{ marginBottom: 4 }}>{s}</Tag>)}
							</div>
						</div>
					)}
					{p.languages_spoken?.length > 0 && (
						<Text style={{ fontSize: 13 }}><strong>Languages:</strong> {p.languages_spoken.join(", ")}</Text>
					)}
					{p.biography && (
						<div>
							<Text strong style={{ fontSize: 13 }}>About</Text>
							<Paragraph style={{ fontSize: 13, color: "#555", marginTop: 4, marginBottom: 0 }}>{p.biography}</Paragraph>
						</div>
					)}
					<Text style={{ fontSize: 12, color: "#52c41a" }}>
						<CheckCircleOutlined /> {availabilityText} ({availableSlots} slot{availableSlots !== 1 ? "s" : ""})
					</Text>
				</Space>
				<div style={{ textAlign: "right" }}>
					<Button
						type="primary"
						onClick={() => {
							setPreviewPractitioner(null);
							handleConsultantSelect(p);
						}}
					>
						Select
					</Button>
				</div>
			</Modal>
		);
	};

	const renderScreeningStep = () => (
		<div>
			<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
				<Button size="small" type="primary" icon={<ArrowLeftOutlined />} onClick={resetScreening}>
					Back
				</Button>
			</div>

			<ScreeningStep
				appointmentTypeId={selectedAppointmentType!.id}
				onPass={handleScreeningPass}
				// The fallback fires only when the backend sent no message — a blank terminal card tells the patient nothing.
				onBlocked={(reason) => setScreeningBlocked(reason || "Please contact the clinic to book this appointment.")}
			/>
		</div>
	);

	// Terminal. The message is clinic-authored per appointment type and is rendered verbatim; the
	// question and answer that caused it are deliberately never sent to the patient.
	const renderScreeningBlocked = () => (
		<div data-testid="portal-screening-blocked" style={{ textAlign: "center", padding: "24px 16px" }}>
			<Alert type="warning" showIcon message={screeningBlocked} style={{ textAlign: "left", marginBottom: 24 }} />
			<Button type="primary" data-testid="portal-screening-choose-another" onClick={resetScreening}>
				Choose another appointment type
			</Button>
		</div>
	);

	const renderConsultantSelection = () => (
		<div>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
				<div>
					<Title level={4} style={{ margin: 0 }}>
						Select Your Healthcare Provider
					</Title>
					<Text type="secondary" style={{ fontSize: 13 }}>
						Choose from our available healthcare professionals
					</Text>
				</div>
				<Button size="small" type="primary" icon={<ArrowLeftOutlined />} onClick={() => goToStep(0)}>
					Back
				</Button>
			</div>

			{isLoading ? (
				<InlineLoading minHeight={160} />
			) : practitioners.length === 0 ? (
				<EmptyState title="No providers available" icon={<UserOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
			) : (
				<div style={{ maxHeight: 400, overflowY: "auto" }}>
					<Space direction="vertical" size="small" style={{ width: "100%" }}>
						{practitioners.map(renderConsultantCard)}
					</Space>
				</div>
			)}
			{renderPractitionerModal()}
		</div>
	);

	const renderCalendar = () => {
		const availableDates = getAvailableDates();

		if (!selectedPractitioner) {
			return (
				<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: 32, textAlign: "center" }}>
					<UserOutlined style={{ fontSize: 48, color: "#bfbfbf", marginBottom: 16 }} />
					<Text type="secondary">Please select a provider first</Text>
				</div>
			);
		}

		const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
		const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
		const startDate = new Date(monthStart);
		startDate.setDate(startDate.getDate() - startDate.getDay());

		const days = [];
		let day = new Date(startDate);

		while (day <= monthEnd || day.getDay() !== 0) {
			const dateStr = formatLocalDateString(day);
			// The clinic's booking window bounds the calendar: a date past `booking_window_days` is not
			// selectable even if a stale slot list still carries it.
			const isAvailable = availableDates.includes(dateStr) && isWithinBookingWindow(day, bookingPolicy);
			const isSelected = dateStr === selectedDate;
			const isToday = isDateToday(day);
			const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

			const dayData = selectedPractitioner.Available_times.find((d) => d.date === dateStr);
			let slotsCount = dayData?.slots.length || 0;
				
			days.push(
				<div
					key={dateStr}
					className={`booking-calendar-day ${isSelected ? "selected" : ""} ${isToday ? "today" : ""}`}
					style={{
						padding: 8,
						textAlign: "center",
						cursor: isAvailable && isCurrentMonth ? "pointer" : "not-allowed",
						backgroundColor: isSelected ? "#1890ff" : isAvailable && isCurrentMonth ? "#f0f9ff" : "#fafafa",
						color: isSelected ? "white" : isAvailable && isCurrentMonth ? "#1890ff" : "#bfbfbf",
						border: isToday ? "2px solid #faad14" : "1px solid #d9d9d9",
						borderRadius: 4,
						fontSize: 12,
						minHeight: 50,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
					}}
					onClick={() => isAvailable && isCurrentMonth && handleDateSelect(dateStr)}
				>
					<div style={{ fontWeight: "bold" }}>{day.getDate()}</div>
					{isAvailable && isCurrentMonth && <div style={{ fontSize: 10, marginTop: 2 }}>{slotsCount} slots</div>}
				</div>
			);

			day.setDate(day.getDate() + 1);
			if (days.length >= 42) break;
		}

		return (
			<div>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
					<Button size="small" type="primary" icon={<ArrowLeftOutlined />} onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
						Previous
					</Button>
					<Title level={5} style={{ margin: 0 }}>
						{currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
					</Title>
					<Button size="small" type="primary" icon={<ArrowRightOutlined />} onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
						Next
					</Button>
				</div>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 8 }}>
					{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
						<div key={day} style={{ textAlign: "center", fontWeight: "bold", fontSize: 12, padding: 8 }}>
							{day}
						</div>
					))}
				</div>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>{days}</div>
			</div>
		);
	};

	const renderTimeSlots = () => {
		if (!selectedDate || availableTimesForDate.length === 0) return null;

		return (
			<div style={{ marginTop: 24 }}>
				<Title level={5}>Available Times</Title>
				<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: bookingPolicy?.timezone ? 2 : 8 }}>
					Times shown in your local timezone: <strong>{Intl.DateTimeFormat().resolvedOptions().timeZone}</strong>
				</Text>
				{/* The slots above stay in the PATIENT's timezone. This line describes the CLINIC's hours,
				    which is the one thing `policy.timezone` actually tells us. */}
				{bookingPolicy?.timezone && (
					<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
						Clinic hours are set in: <strong>{bookingPolicy.timezone}</strong>
					</Text>
				)}
				<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
					{availableTimesForDate.map((slot) => {
						const isSelected = selectedTimeSlot?.time === slot.time;
						return (
							<Button
								key={slot.time}
								size="small"
								type={"primary"}
								className={`booking-time-slot ${isSelected ? "selected" : ""}`}
								onClick={() => handleTimeSlotSelect(slot)}
								style={{
									height: 36,
									fontSize: 12,
								}}
							>
								{slot.display_time || formatLocalTime(slot.datetime)}
							</Button>
						);
					})}
				</div>
			</div>
		);
	};

	const renderDateTimeSelection = () => (
		<div>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
				<div>
					<Title level={4} style={{ margin: 0 }}>
						Select Date & Time
					</Title>
					<Text type="secondary" style={{ fontSize: 13 }}>
						Choose from {selectedPractitioner?.first_name}'s available times
					</Text>
				</div>
				<Button size="small" type="primary" icon={<ArrowLeftOutlined />} onClick={() => goToStep(1)}>
					Back
				</Button>
			</div>

			{renderCalendar()}
			{renderTimeSlots()}
		</div>
	);

	const renderPaymentSection = () => {
		const price = selectedAppointmentType?.default_price ? parseFloat(selectedAppointmentType.default_price) : 0;

		return (
			<div>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
					<div>
						<Title level={4} style={{ margin: 0 }}>
							Payment
						</Title>
						<Text type="secondary" style={{ fontSize: 13 }}>
							Complete your payment to confirm the booking
						</Text>
					</div>
					<Button size="small" type="primary" icon={<ArrowLeftOutlined />} onClick={() => goToStep(2)}>
						Back
					</Button>
				</div>

				{loadingPayment ? (
					<InlineLoading minHeight={160} />
				) : (
					<>
						<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
							<Space direction="vertical" size="small" style={{ width: "100%" }}>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Text strong>Appointment Fee</Text>
									<Text strong style={{ fontSize: 18, color: "#1890ff" }}>
										${price.toFixed(2)}
									</Text>
								</div>
								<Divider style={{ margin: "8px 0" }} />
								<div style={{ fontSize: 12, color: "#999" }}>
									<div>Type: {selectedAppointmentType?.name}</div>
									<div>Duration: {selectedAppointmentType?.duration_minutes} minutes</div>
								</div>
							</Space>
						</div>

						{!financeCustomer ? (
							<Alert message="Setting up your payment account..." type="info" showIcon />
						) : (
							<>
								<Title level={5} style={{ marginBottom: 12 }}>
									Select Payment Method
								</Title>

								{paymentMethods.length === 0 ? (
									<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
										<CreditCardOutlined style={{ fontSize: 48, color: "#bfbfbf", marginBottom: 16 }} />
										<Paragraph>No payment methods available</Paragraph>
										<Button type="primary" icon={<PlusOutlined />} onClick={() => setAddPaymentModalVisible(true)}>
											Add Payment Method
										</Button>
									</div>
								) : (
									<>
										<Radio.Group value={selectedPaymentMethod} onChange={(e) => setSelectedPaymentMethod(e.target.value)} style={{ width: "100%", marginBottom: 16 }}>
											<Space direction="vertical" size="small" style={{ width: "100%" }}>
												{paymentMethods.map((method) => (
													<div
														key={method.id}
														style={{
															cursor: "pointer",
															border: selectedPaymentMethod === method.id ? "2px solid var(--mg-primary)" : "1px solid var(--mg-border)",
															backgroundColor: selectedPaymentMethod === method.id ? "var(--mg-primary-bg)" : "transparent",
															borderRadius: 8,
															padding: "12px 16px",
														}}
														onClick={() => setSelectedPaymentMethod(method.id)}
													>
														<Radio value={method.id} style={{ width: "100%" }}>
															<Row align="middle" gutter={[12, 0]}>
																<Col>
																	<CreditCardOutlined style={{ fontSize: 24 }} />
																</Col>
																<Col flex={1}>
																	<div style={{ fontWeight: 500 }}>
																		{method.card?.brand.toUpperCase()} •••• {method.card?.last4}
																	</div>
																	<div style={{ fontSize: 12, color: "#999" }}>
																		Expires {method.card?.exp_month}/{method.card?.exp_year}
																	</div>
																</Col>
																{financeCustomer?.invoice_settings?.default_payment_method === method.id && (
																	<Col>
																		<Tag color="blue" icon={<CheckCircleOutlined />}>
																			Default
																		</Tag>
																	</Col>
																)}
															</Row>
														</Radio>
													</div>
												))}
											</Space>
										</Radio.Group>

										<Button icon={<PlusOutlined />} onClick={() => setAddPaymentModalVisible(true)} style={{ width: "100%" }} size="small">
											Add New Payment Method
										</Button>
									</>
								)}
							</>
						)}
					</>
				)}

				<div style={{ marginTop: 16, textAlign: "right" }}>
					<Button type="primary" onClick={() => goToStep(4)} disabled={!selectedPaymentMethod || loadingPayment} icon={<ArrowRightOutlined />} style={{ marginLeft: 8 }}>
						Continue to Review
					</Button>
				</div>

				{financeCustomer  && (
					<AddPaymentMethodModal
						visible={addPaymentModalVisible}
						onClose={() => setAddPaymentModalVisible(false)}
						onSuccess={() => {
							setAddPaymentModalVisible(false);
							setPaymentRefreshTrigger((prev) => prev + 1);
						}}
						customerId={financeCustomer.id}
					/>
				)}
			</div>
		);
	};

	const renderReview = () => {
		const price = selectedAppointmentType?.default_price ? parseFloat(selectedAppointmentType.default_price) : 0;
		const selectedPaymentMethodDetails = paymentMethods.find((m) => m.id === selectedPaymentMethod);

		return (
			<div>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
					<div>
						<Title level={4} style={{ margin: 0 }}>
							Review Your Booking
						</Title>
						<Text type="secondary" style={{ fontSize: 13 }}>
							Please confirm your appointment details
						</Text>
					</div>
					<Button size="small" type="primary" icon={<ArrowLeftOutlined />} onClick={() => goToStep(requiresPayment() && financeEnabled ? 3 : 2)}>
						Back
					</Button>
				</div>

				<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
					<Title level={5} style={{ margin: 0, marginBottom: 12 }}>
						Patient Information
					</Title>
					<Text>
						{patientInfo.first_name} {patientInfo.last_name}
					</Text>
					<br />
					<Text type="secondary">{patientInfo.email}</Text>
					<br />
					<Text type="secondary">{patientInfo.phone}</Text>
				</div>

				{selectedAppointmentType && (
					<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
						<Title level={5} style={{ margin: 0, marginBottom: 12 }}>
							Appointment Type
						</Title>
						<Text>{selectedAppointmentType.name}</Text>
						<br />
						<Text type="secondary">{selectedAppointmentType.duration_minutes} minutes</Text>
						<br />
						<Text type="secondary">{selectedAppointmentType.telehealth_enabled ? "Online" : "In-person"}</Text>
						{price > 0 && (
							<>
								<br />
								<Text strong style={{ color: "#1890ff" }}>
									Fee: ${price.toFixed(2)}
								</Text>
							</>
						)}
					</div>
				)}

				<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
					<Title level={5} style={{ margin: 0, marginBottom: 12 }}>
						Healthcare Provider
					</Title>
					<Text>
						{selectedPractitioner?.first_name} {selectedPractitioner?.last_name}
					</Text>
					<br />
					{selectedPractitioner?.primary_specialty && <Text type="secondary">{selectedPractitioner.primary_specialty}</Text>}
				</div>

				<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
					<Title level={5} style={{ margin: 0, marginBottom: 12 }}>
						Date & Time
					</Title>
					<Text>
						<strong>Date:</strong> {selectedDate && new Date(selectedDate).toLocaleDateString()}
					</Text>
					<br />
					<Text>
						<strong>Time:</strong> {selectedTimeSlot?.display_time || (selectedTimeSlot && formatLocalTime(selectedTimeSlot.datetime))}
						{selectedTimeSlot && (
							<Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
								({Intl.DateTimeFormat().resolvedOptions().timeZone})
							</Text>
						)}
					</Text>
				</div>

				{requiresPayment() && financeEnabled && selectedPaymentMethodDetails && (
					<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
						<Title level={5} style={{ margin: 0, marginBottom: 12 }}>
							Payment
						</Title>
						<Text>
							<strong>Amount:</strong> ${price.toFixed(2)}
						</Text>
						<br />
						<Text>
							<strong>Payment Method:</strong> {selectedPaymentMethodDetails.card?.brand.toUpperCase()} •••• {selectedPaymentMethodDetails.card?.last4}
						</Text>
					</div>
				)}

				{requiresPayment() && financeEnabled && (
					<Alert
						message="Payment Authorization"
						description={`Your payment method will be charged $${price.toFixed(2)} upon confirmation. You will receive a receipt via email.`}
						type="info"
						showIcon
						icon={<CreditCardOutlined />}
						style={{ marginBottom: 16 }}
					/>
				)}

				{selectedAppointmentType && (
					<div style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", textAlign: "center", marginBottom: 16 }}>
						<Text type="secondary" style={{ fontSize: 12 }}>
							<strong>Your Timezone:</strong> {DateTimeUtils.getUserTimezone()} ({DateTimeUtils.getTimezoneOffset()})
							<br />
							All times shown are in your local timezone
						</Text>
					</div>
				)}
			</div>
		);
	};

	const getCurrentStepContent = () => {
		if (screeningBlocked) return renderScreeningBlocked();

		// Step 0 is always the type list, even with a screened type still selected.
		if (screeningRequired && currentStep > 0) return renderScreeningStep();

		if (requiresPayment() && financeEnabled) {
			// Flow with payment: 0=type, 1=consultant, 2=date/time, 3=payment, 4=review
			switch (currentStep) {
				case 0:
					return renderAppointmentTypeSelection();
				case 1:
					return renderConsultantSelection();
				case 2:
					return renderDateTimeSelection();
				case 3:
					return renderPaymentSection();
				case 4:
					return renderReview();
				default:
					return renderAppointmentTypeSelection();
			}
		} else {
			// Flow without payment: 0=type, 1=consultant, 2=date/time, 3=review
			switch (currentStep) {
				case 0:
					return renderAppointmentTypeSelection();
				case 1:
					return renderConsultantSelection();
				case 2:
					return renderDateTimeSelection();
				case 3:
					return renderReview();
				default:
					return renderAppointmentTypeSelection();
			}
		}
	};

	const isReviewStep = () => {
		if (requiresPayment() && financeEnabled) {
			return currentStep === 4;
		}
		return currentStep === 3;
	};

	return (
		<DetailDrawer
			title="Book New Appointment"
			onClose={() => {
				if (!isSubmitting) {
					resetForm();
					onHide();
				}
			}}
			open={visible}
			width="50vw"
			footer={
				<Space>
					<Button
						onClick={() => {
							if (!isSubmitting) {
								resetForm();
								onHide();
							}
						}}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					{isReviewStep() && (
						<Button type="primary" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
							{isSubmitting ? "Booking..." : requiresPayment() && financeEnabled ? "Pay & Book Appointment" : "Book Appointment"}
						</Button>
					)}
				</Space>
			}
		>
			<Space direction="vertical" size="middle" style={{ width: "100%" }}>
				{error && <Alert message={error} type="error" closable onClose={() => setError("")} showIcon />}

				{renderProgressBar()}

				{getCurrentStepContent()}
			</Space>
		</DetailDrawer>
	);
};

export default AppointmentBookingModal;
